import React, { FC, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { get } from 'lodash';
import { createPortal } from 'react-dom';
import FocusLock from 'react-focus-lock';
import { time } from 'uniqid';

import { EVENTS, KEY_CODES } from '@trip-ux/constants';
import { setAriaHidden, showComponent } from '@trip-ux/common';

const MODAL_OPEN = 'modal_open';

export const modalContainerId = 'modal-container';

export interface ModalProps {
  children?: React.ReactNode;
  onClose?: (state: boolean) => void;
  isOpen: boolean;
  id?: string;
  closeAriaLabel?: string;
  className?: string;
  ariaLabel: string;
  ariaLabelledby: string;
  ariaDescribedby: string;
  appElement?: string;
  isCloseable?: boolean;
  closeOnBackdropClick?: boolean;
  navigateOnClose?: boolean;
  returnFocusComponent?: RefObject<HTMLElement>;
  isIdleReminder?: boolean;
}
export const Modal: FC<ModalProps> = ({
  children,
  onClose,
  isOpen,
  id = time('modal-container-'),
  closeAriaLabel,
  className = 'modal-dialog',
  ariaLabel,
  ariaLabelledby,
  ariaDescribedby,
  isCloseable = true,
  appElement = '#root',
  closeOnBackdropClick = true,
  navigateOnClose = false,
  returnFocusComponent,
  isIdleReminder = false
}) => {
  const [shouldFocusOnClose, setShouldFocusOnClose] = useState(false);
  const contentRef = useRef<HTMLDivElement>();

  const closeModal = useCallback(() => {
    setShouldFocusOnClose(true);
    onClose(false);
  }, [onClose]);

  const handleESCKeyPress = useCallback(
    event => {
      if (isOpen && event.keyCode === KEY_CODES.ESCAPE) {
        closeModal();
      }
    },
    [isOpen, closeModal]
  );

  useEffect(() => {
    window.addEventListener(EVENTS.KEY_DOWN, handleESCKeyPress);
    return () => {
      window.removeEventListener(EVENTS.KEY_DOWN, handleESCKeyPress);
    };
  }, [handleESCKeyPress]);

  useEffect(() => {
    const componentToFocus = get(returnFocusComponent, 'current');
    if (!isOpen && shouldFocusOnClose && componentToFocus) {
      componentToFocus.focus();
      setShouldFocusOnClose(false);
    }
  }, [isOpen, shouldFocusOnClose, returnFocusComponent]);

  useEffect(() => {
    isOpen && contentRef && contentRef.current && contentRef.current.focus();
  }, [isOpen, contentRef]);

  useEffect(() => {
    const elem = document.querySelector(appElement);
    const elembody = document.querySelector('body');
    elembody.classList.toggle(MODAL_OPEN, isOpen);
    isOpen && setAriaHidden(elem, 'true');
    !isOpen && setAriaHidden(elem, 'false');
    return () => {
      if (navigateOnClose) {
        setAriaHidden(elem, 'true');
        elembody.classList.toggle(MODAL_OPEN, false);
      } else {
        setAriaHidden(elem, 'false');
        elembody.classList.toggle(MODAL_OPEN, isOpen);
      }
    };
  }, [appElement, isOpen, navigateOnClose]);

  return createPortal(
    <>
      {showComponent(
        isOpen,
        <div
          className={`modal d-block${isIdleReminder ? ' top-modal' : ''}`}
          onClick={() => {
            closeOnBackdropClick && onClose(false);
          }}>
          <FocusLock autoFocus={false} returnFocus>
            <div
              className={`${className}`}
              id={id}
              data-testid={id}
              role="dialog"
              ref={contentRef}
              aria-modal="true"
              aria-label={ariaLabel}
              aria-labelledby={ariaLabelledby}
              aria-describedby={ariaDescribedby}
              tabIndex={-1}>
              <div className="modal-content">
                {showComponent(
                  isCloseable,
                  <div className="close-button">
                    <button
                      aria-label={closeAriaLabel}
                      data-testid="close-button"
                      type="button"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        closeModal();
                      }}
                    />
                  </div>
                )}
                <div id={modalContainerId} className="modal-container">
                  {children}
                </div>
              </div>
            </div>
          </FocusLock>
        </div>
      )}
      <div />
    </>,
    document.body
  );
};

Modal.displayName = 'Modal';
export default Modal;
